import React, { useState, useEffect, useRef } from 'react';
import client from '../api/client';
import * as gdriveApi from '../api/gdrive';
import { openFolderPicker, openMultiImagePicker } from '../utils/googlePicker';

const isElectron = !!window.electronAPI;

export default function SortPage() {
  const [tab, setTab] = useState(isElectron ? 'local' : 'drive');

  /* ── ローカル（Electron のみ） ── */
  const [srcFolder, setSrcFolderRaw] = useState(() => localStorage.getItem('srcFolder') ?? '');
  const [outFolder, setOutFolderRaw] = useState(() => localStorage.getItem('outFolder') ?? '');
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localProgress, setLocalProgress] = useState({ current: 0, total: 0 });
  const [localFailures, setLocalFailures] = useState([]);
  const cancelLocalRef = useRef(false);

  const setSrcFolder = (v) => { setSrcFolderRaw(v); localStorage.setItem('srcFolder', v); };
  const setOutFolder = (v) => { setOutFolderRaw(v); localStorage.setItem('outFolder', v); };

  /* ── Google Drive ── */
  const [authed, setAuthed]           = useState(false);
  const [authPolling, setAuthPolling] = useState(false);
  // 振り分け元: Picker で複数選択した画像ファイル一覧 [{id, name}, ...]
  // drive.file 制約により folder pick だけでは中身を読めないため、明示的に複数選択させる
  const [srcDriveFiles, setSrcDriveFiles] = useState([]);
  // 保存先: フォルダ {id, name}
  const [outDriveFolder, setOutDriveFolderRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem('outDriveFolder') || 'null'); } catch { return null; }
  });
  const [pickerLoading, setPickerLoading] = useState(false);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveError, setDriveError]   = useState('');

  const setOutDriveFolder = (v) => { setOutDriveFolderRaw(v); localStorage.setItem('outDriveFolder', JSON.stringify(v)); };
  const pollRef = useRef(null);

  /* ── 結果 ── */
  const [results, setResults] = useState(null);

  const checkAuth = async () => {
    try {
      const { authenticated } = await gdriveApi.status();
      setAuthed(authenticated);
      if (authenticated) {
        clearInterval(pollRef.current);
        setAuthPolling(false);
      }
    } catch {}
  };

  // Picker で振り分け対象の写真を複数選択
  const pickDriveSrcFiles = async () => {
    setPickerLoading(true); setDriveError('');
    try {
      const { access_token } = await gdriveApi.getAccessToken();
      const picked = await openMultiImagePicker({ accessToken: access_token });
      if (picked) setSrcDriveFiles(picked.files);
    } catch (e) {
      setDriveError(e.response?.data?.detail ?? e.message ?? '写真選択に失敗しました');
    } finally {
      setPickerLoading(false);
    }
  };

  // Picker で保存先フォルダを選択
  const pickDriveOutFolder = async () => {
    setPickerLoading(true); setDriveError('');
    try {
      const { access_token } = await gdriveApi.getAccessToken();
      const picked = await openFolderPicker({ accessToken: access_token });
      if (picked) setOutDriveFolder(picked);
    } catch (e) {
      setDriveError(e.response?.data?.detail ?? e.message ?? 'フォルダ選択に失敗しました');
    } finally {
      setPickerLoading(false);
    }
  };

  useEffect(() => { checkAuth(); return () => clearInterval(pollRef.current); }, []);

  const openAuth = async () => {
    try {
      const { auth_url } = await gdriveApi.getAuthUrl();
      if (window.electronAPI?.openExternal) {
        window.electronAPI.openExternal(auth_url);
      } else {
        window.open(auth_url, '_blank');
      }
      setAuthPolling(true);
      pollRef.current = setInterval(checkAuth, 2000);
    } catch (e) {
      setDriveError(e.response?.data?.detail ?? '認証URLの取得に失敗しました');
    }
  };

  const pickFolder = async (setter) => {
    try {
      if (window.electronAPI?.selectFolder) {
        const path = await window.electronAPI.selectFolder();
        if (path) setter(path);
      }
    } catch (e) {
      setLocalError(`フォルダ選択に失敗しました: ${e.message ?? e}`);
    }
  };

  const cancelLocalSort = () => { cancelLocalRef.current = true; };

  const handleLocalSort = async () => {
    if (!srcFolder) { setLocalError('振り分け元フォルダを指定してください'); return; }
    if (!outFolder) { setLocalError('保存先フォルダを指定してください'); return; }
    setLocalLoading(true);
    setLocalError('');
    setResults(null);
    setLocalFailures([]);
    setLocalProgress({ current: 0, total: 0 });
    cancelLocalRef.current = false;

    const eApi = window.electronAPI;
    const failures = [];

    let files = [];
    try {
      files = await eApi.listImages(srcFolder);
    } catch (e) {
      setLocalError(`画像一覧の取得に失敗しました: ${e.message ?? e}`);
      setLocalLoading(false);
      return;
    }
    if (!files.length) {
      setLocalError('画像ファイルが見つかりません');
      setLocalLoading(false);
      return;
    }

    setLocalProgress({ current: 0, total: files.length });

    const resultList = [];
    let sortedCount = 0;

    for (let i = 0; i < files.length; i++) {
      if (cancelLocalRef.current) break;
      const filePath = files[i];
      const fileName = filePath.split(/[\\/]/).pop();
      setLocalProgress({ current: i + 1, total: files.length });

      try {
        const b64 = await eApi.readImageBase64(filePath);
        const blob = await fetch(`data:image/jpeg;base64,${b64}`).then(r => r.blob());
        const formData = new FormData();
        formData.append('file', blob, fileName);

        const res = await fetch(
          (process.env.REACT_APP_API_URL || '') + '/photos/sort-image',
          { method: 'POST', credentials: 'include', body: formData }
        );

        if (!res.ok) {
          // 上限超過 (403) や認証エラー (401) は処理を中断
          if (res.status === 401 || res.status === 402 || res.status === 403) {
            const data = await res.json().catch(() => ({}));
            setLocalError(data.detail ?? `処理を中断しました（HTTP ${res.status}）`);
            break;
          }
          // それ以外（個別エラー）は失敗として記録して継続
          const data = await res.json().catch(() => ({}));
          failures.push({ file_name: fileName, error: data.detail ?? `HTTP ${res.status}` });
          resultList.push({ file_name: fileName, matched_children: [] });
          continue;
        }

        const data = await res.json();
        const matched = data.matched_children || [];

        // 振り分け不要ユーザーが写っている写真はコピーせずスキップ
        if (data.excluded) {
          resultList.push({ file_name: fileName, matched_children: [], excluded: true });
          continue;
        }

        for (const childName of matched) {
          try {
            await eApi.copyFile(filePath, outFolder + '/' + childName);
          } catch (copyErr) {
            failures.push({ file_name: fileName, error: `コピー失敗 (${childName}): ${copyErr.message ?? copyErr}` });
          }
        }

        resultList.push({ file_name: fileName, matched_children: matched });
        if (matched.length) sortedCount++;
      } catch (e) {
        failures.push({ file_name: fileName, error: e.message ?? String(e) });
        resultList.push({ file_name: fileName, matched_children: [] });
      }
    }

    // 使用実績を記録（中断時も成功分を記録）
    if (sortedCount > 0) {
      try {
        await client.post('/photos/record-sort', { sorted_count: sortedCount });
      } catch (e) {
        failures.push({ file_name: '(使用実績の記録)', error: e.response?.data?.detail ?? e.message ?? String(e) });
      }
    }

    setLocalFailures(failures);
    setResults({
      total: resultList.length,
      sorted_count: sortedCount,
      unmatched_count: resultList.length - sortedCount,
      results: resultList,
    });
    if (cancelLocalRef.current) {
      setLocalError(`処理を中断しました（${resultList.length} / ${files.length} 件処理済み）`);
    }
    setLocalLoading(false);
  };

  const handleDriveSort = async () => {
    if (srcDriveFiles.length === 0) { setDriveError('振り分け対象の写真を選択してください'); return; }
    if (!outDriveFolder?.id)        { setDriveError('保存先フォルダを選択してください'); return; }
    setDriveLoading(true); setDriveError(''); setResults(null);
    try {
      setResults(await gdriveApi.sort({
        file_ids: srcDriveFiles.map(f => f.id),
        output_folder_id: outDriveFolder.id,
      }));
    } catch (e) {
      setDriveError(e.response?.data?.detail ?? '振り分けに失敗しました');
    } finally {
      setDriveLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header animate-in">
        <h1 className="page-title">写真振り分け</h1>
        <p className="page-subtitle">登録された顔情報をもとに写真を自動で振り分けます</p>
      </div>

      <div className="page-body two-col" style={{ alignItems: 'flex-start' }}>
        {/* ── 設定パネル ── */}
        <div className="two-col-left animate-in-delay-1" style={{ width: 340 }}>
          {/* タブバー：Electronのみローカルタブを表示 */}
          {isElectron && (
            <div className="tab-bar">
              <button className={`tab-btn${tab === 'local' ? ' active' : ''}`} onClick={() => setTab('local')}>
                ローカル
              </button>
              <button className={`tab-btn${tab === 'drive' ? ' active' : ''}`} onClick={() => setTab('drive')}>
                Google Drive
              </button>
            </div>
          )}

          {/* ローカルタブ（Electron のみ） */}
          {tab === 'local' && isElectron && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">振り分け元フォルダ</label>
                <div className="folder-input-group">
                  <input className="form-input" type="text" placeholder="C:\photos\event" value={srcFolder} onChange={e => setSrcFolder(e.target.value)} />
                  <button type="button" className="btn btn-secondary" onClick={() => pickFolder(setSrcFolder)}>選択</button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">保存先フォルダ</label>
                <div className="folder-input-group">
                  <input className="form-input" type="text" placeholder="C:\photos\sorted" value={outFolder} onChange={e => setOutFolder(e.target.value)} />
                  <button type="button" className="btn btn-secondary" onClick={() => pickFolder(setOutFolder)}>選択</button>
                </div>
              </div>
              {localError && <div className="msg msg-error">{localError}</div>}
              {localLoading && localProgress.total > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span>{localProgress.current} / {localProgress.total} 件処理中…</span>
                    <span>{Math.round((localProgress.current / localProgress.total) * 100)}%</span>
                  </div>
                  <div style={{ height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${(localProgress.current / localProgress.total) * 100}%`,
                      background: 'linear-gradient(90deg, #FF6B9D, #9B5FE3)',
                      transition: 'width 0.2s',
                    }} />
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={handleLocalSort} disabled={localLoading} style={{ flex: 1, padding: '11px' }}>
                  {localLoading ? <><span className="spinner" />振り分け中…</> : '振り分け実行'}
                </button>
                {localLoading && (
                  <button
                    type="button"
                    onClick={cancelLocalSort}
                    style={{
                      padding: '11px 16px', borderRadius: 12, border: '1.5px solid #fc8181',
                      background: '#fff', color: '#c53030',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    中断
                  </button>
                )}
              </div>
              {localFailures.length > 0 && (
                <details style={{
                  background: '#fff5f5', border: '1px solid #fed7d7',
                  borderRadius: 10, padding: '10px 12px', fontSize: 12,
                }}>
                  <summary style={{ cursor: 'pointer', color: '#c53030', fontWeight: 700 }}>
                    ⚠️ {localFailures.length} 件のエラー
                  </summary>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#742a2a', lineHeight: 1.6 }}>
                    {localFailures.slice(0, 30).map((f, i) => (
                      <li key={i}><strong>{f.file_name}:</strong> {f.error}</li>
                    ))}
                    {localFailures.length > 30 && (
                      <li>…ほか {localFailures.length - 30} 件</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Google Drive タブ */}
          {(tab === 'drive' || !isElectron) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`auth-badge ${authed ? 'ok' : 'ng'}`}>
                  <span className="auth-dot" />
                  {authed ? '接続済み' : '未接続'}
                </span>
                {!authed && (
                  <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={openAuth} disabled={authPolling}>
                    {authPolling ? <><span className="spinner spinner-dark" />認証待ち…</> : 'Google Drive に接続'}
                  </button>
                )}
              </div>

              {authed && (
                <>
                  <div className="form-group">
                    <label className="form-label">振り分け対象の写真</label>
                    <button
                      className="btn btn-secondary"
                      style={{ width: '100%', padding: '10px', justifyContent: 'flex-start' }}
                      onClick={pickDriveSrcFiles}
                      disabled={pickerLoading}
                    >
                      {srcDriveFiles.length > 0
                        ? <>🖼 {srcDriveFiles.length} 枚選択中　<span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(選び直す)</span></>
                        : (pickerLoading ? <><span className="spinner spinner-dark" />読込中…</> : '＋ 写真を選ぶ')}
                    </button>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.5 }}>
                      フォルダに入って写真を複数選択できます。<br />
                      💡 先頭をクリック → <strong>Shift+Click で末尾</strong> を選ぶと範囲を一括選択
                    </p>
                    {srcDriveFiles.length > 0 && (
                      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', maxHeight: 60, overflowY: 'auto', background: 'var(--border-light)', borderRadius: 8, padding: '4px 8px' }}>
                        {srcDriveFiles.slice(0, 5).map(f => f.name).join('、')}
                        {srcDriveFiles.length > 5 && ` ほか ${srcDriveFiles.length - 5} 枚`}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Drive フォルダ（保存先）</label>
                    <button
                      className="btn btn-secondary"
                      style={{ width: '100%', padding: '10px', justifyContent: 'flex-start' }}
                      onClick={pickDriveOutFolder}
                      disabled={pickerLoading}
                    >
                      {outDriveFolder?.name
                        ? <>📁 {outDriveFolder.name}　<span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(変更)</span></>
                        : (pickerLoading ? <><span className="spinner spinner-dark" />読込中…</> : '＋ フォルダを選ぶ')}
                    </button>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                      子供名のサブフォルダが自動作成されます
                    </p>
                  </div>
                  {driveError && <div className="msg msg-error">{driveError}</div>}
                  <button className="btn btn-primary" onClick={handleDriveSort} disabled={driveLoading} style={{ width: '100%', padding: '11px' }}>
                    {driveLoading ? <><span className="spinner" />振り分け中…</> : '振り分け実行'}
                  </button>
                </>
              )}

              {!authed && driveError && <div className="msg msg-error">{driveError}</div>}
            </div>
          )}
        </div>

        {/* ── 縦区切り ── */}
        <div className="two-col-divider" />

        {/* ── 結果パネル ── */}
        <div className="two-col-right animate-in-delay-2">
          {!results ? (
            <div className="empty-state" style={{ paddingTop: 64 }}>
              <div className="empty-icon">⊞</div>
              <div className="empty-text">振り分け結果がここに表示されます</div>
            </div>
          ) : (
            <div className="animate-in">
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-value">{results.total}</div>
                  <div className="stat-label">合計</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value" style={{ color: 'var(--success)' }}>{results.sorted_count}</div>
                  <div className="stat-label">振り分け済み</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value" style={{ color: 'var(--text-muted)' }}>{results.unmatched_count}</div>
                  <div className="stat-label">未マッチ</div>
                </div>
              </div>

              <div className="result-list">
                {results.results.map((r, i) => (
                  <div key={i} className="result-item">
                    <span className="result-filename">{r.file_name}</span>
                    <div className="result-tags">
                      {r.excluded
                        ? <span className="tag" style={{ color: '#c53030', background: '#fff5f5', border: '1px solid #fed7d7' }}>除外</span>
                        : r.matched_children.length
                          ? r.matched_children.map((c, j) => <span key={j} className="tag tag-match">{c}</span>)
                          : <span className="tag tag-nomatch">未マッチ</span>
                      }
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
